import React, { PropsWithChildren, ReactElement, ReactNode, useState } from 'react';
import clsx from 'clsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgCircleInfo from '@icons/circle-info.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import COButton from '@components/FigmaComponents/COButton.tsx';

export interface DeferredSectionConfig {
  /** Sentence shown in the band explaining what is behind the reveal. */
  description: string;
  /** Inline link text that reveals the body. */
  revealLabel: string;
  dataTestId?: string;
}

interface ComponentProps {
  heading: string | ReactNode;
  icon: ReactElement<{ color: string }>;
  iconColor?: string;
  headerRight?: ReactNode;
  dataTestId?: string;
  /** When set, children are not mounted until the user reveals the section. */
  deferred?: DeferredSectionConfig;
}

const SectionPanel: React.FC<PropsWithChildren<ComponentProps>> = ({ heading, icon, iconColor, headerRight, dataTestId, deferred, children }) => {
  const [revealed, setRevealed] = useState(false);
  const collapsed = !!deferred && !revealed;

  const toggleButton = (
    <COButton
      onClick={() => setRevealed(!revealed)}
      dataTestId={deferred?.dataTestId}
      icon={<SvgChevron className={clsx('w-4 h-4 text-content-brand', revealed && 'rotate-180')} />}
    />
  );

  return (
    <div className={'bg-base-base px-[16px] py-[21px] flex flex-col gap-4'} data-testid={dataTestId}>
      <div className={'flex flex-row items-center justify-between gap-4'}>
        <COHeadline variant={'h2'} title={heading} icon={icon} iconColor={iconColor} />
        {(headerRight || (deferred && revealed)) && (
          <div className={'flex flex-row items-center gap-3'}>
            {headerRight}
            {deferred && revealed && toggleButton}
          </div>
        )}
      </div>
      {collapsed ? (
        <div className={'flex flex-row items-start justify-between gap-4 rounded-2xl bg-base-secondary px-4 py-6'}>
          <div className={'flex flex-row items-center gap-4'}>
            <SvgCircleInfo className={'w-9 h-9 shrink-0 text-content-tertiary'} />
            <div className={'flex flex-row items-center gap-2 text-base text-content-secondary'}>
              <span>{deferred.description}</span>
              <button
                type={'button'}
                onClick={() => setRevealed(true)}
                className={'cursor-pointer appearance-none border-0 bg-transparent p-0 text-content-brand transition-all duration-200 hover:text-complementary-brandDark'}>
                {deferred.revealLabel}
              </button>
            </div>
          </div>
          {toggleButton}
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default SectionPanel;
